import React, { useState, useContext } from "react";
import { MdPayment } from "react-icons/md";
import { FiArrowLeft, FiCheck } from "react-icons/fi";
import { toast } from "react-toastify";
import { httpService } from "../../services/httpService";
import { TokenContext } from "../../context/TokenContext";
import OrderPaymentDrawer from "../../components/Order/OrderPaymentDrawer";

const PaymentAndInvoice = ({
  totals = {},
  orderData = {},
  paymentStatus = "",
  isOrderCancelled = false,
  isAnyAppointmentCancelled = false,
  showPaymentSection = false,
  isPaymentLoading = false,
  paymentDetails = {},
  onPayNow = () => {},
  onBackFromPayment = () => {},
  onPaymentComplete = () => {},
  orderId = null,
  vendorId = null,
  refreshOrderData = () => {},
}) => {
  const totalPaid = parseFloat(orderData?.total_paid_amount || 0);
  const totalAmount = parseFloat(
    orderData?.amount_after_adjustment || (orderData?.grand_total ?? totals.total ?? 0),
  );
  const balanceAmount = Math.max(0, totalAmount - totalPaid);

  const [paymentOrderId, setPaymentOrderId] = useState(null);

  const handlePayNowOrder = (orderId) => {
    setPaymentOrderId(orderId);
  };

  const handlePaymentSuccess = (responseData) => {
    toast.success("Payment recorded successfully!");
  };

  // State for the checkbox
  const [isChecked, setIsChecked] = useState(true);

  // State for invoice sending (matching ServiceInitiationDrawer pattern)
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [invoiceSent, setInvoiceSent] = useState(false);

  // Don't show anything if order is cancelled
  // if (isOrderCancelled || isAnyAppointmentCancelled) {
  //   return null;
  // }
  if (isOrderCancelled) {
    return null;
  }

  // Handle checkbox change
  const handleCheckboxChange = (e) => {
    e.stopPropagation(); // Prevent button click when clicking checkbox
    setIsChecked(!isChecked);
  };

  // Handle invoice sending (matching ServiceInitiationDrawer pattern)
  const handleSendInvoice = async () => {
    if (!orderId) {
      toast.error("Order ID is missing");
      return;
    }

    try {
      setSendingInvoice(true);

      // Prepare payload similar to ServiceInitiationDrawer
      const payload = {
        orderId: orderId,
        // Add other fields if needed based on your backend requirements
      };

      const res = await httpService.post(`/vendororder/sendInvoice`, payload);

      if (res?.status) {
        toast.success("Invoice Sent Successfully!");
        setInvoiceSent(true);
      }
    } catch (err) {
      console.error("Error sending invoice:", err);
      toast.error("Failed to send invoice. Please try again.");
    } finally {
      setSendingInvoice(false);
    }
  };

  // Handle button click - now calls the API function
  const handleButtonClick = () => {
    handleSendInvoice();
  };

  return (
    <div>
      {!showPaymentSection ? (
        /* Balance Due and Send Invoice with optional Pay Now */
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-2">
          <div className="flex justify-between items-center">
            {/* Left: Balance Due */}
            <div className="flex items-center gap-4">
              {balanceAmount > 0 ? (
                <>
                  <span className="text-lg font-bold text-black">
                    Balance Due
                  </span>
                  <span className="text-lg font-bold text-black">
                    ₹ {balanceAmount.toFixed(0)}
                  </span>
                </>
              ) : (
                <span className="text-lg font-bold text-green-600">Paid</span>
              )}
            </div>

            {/* Right: Action Buttons */}
            <div className="flex gap-3">
              {/*
              <button
                onClick={handleButtonClick}
                disabled={sendingInvoice || balanceAmount <= 0}
                className={`py-2 px-2 ${
                  balanceAmount <= 0
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-orange-400 hover:bg-orange-500"
                } text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50`}
              >
                {balanceAmount > 0 && (
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                    onClick={(e) => e.stopPropagation()}
                    className="w-5 h-5 text-white bg-orange-300 border-orange-400 rounded focus:ring-white focus:ring-2 cursor-pointer"
                  />
                )}

                {invoiceSent && (
                  <FiCheck className="text-green-600" size={16} />
                )}

                {sendingInvoice
                  ? "Sending..."
                  : balanceAmount <= 0
                    ? "Invoice Paid"
                    : "Send Invoice"}
              </button>
              */}

              {/* Pay Now Button - Only shown when there's a balance */}
              {balanceAmount > 0 && (
                <button
                  onClick={() => handlePayNowOrder(orderId)}
                  className="py-3 px-2 bg-blue-800 text-white font-semibold rounded-xl hover:bg-blue-600 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <MdPayment className="w-5 h-5" />
                  Pay Now
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Payment Section UI */
        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-4">
                <span className="text-lg font-bold text-red-600">
                  Balance Due
                </span>
                <span className="text-2xl font-bold text-red-600">
                  ₹ {balanceAmount.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={onBackFromPayment}
                className="flex-1 py-3 px-4 bg-gray-600 text-white font-semibold rounded-xl hover:bg-gray-700 transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <FiArrowLeft className="w-4 h-4" />
                Back
              </button>
              <button
                onClick={onPaymentComplete}
                disabled={!paymentDetails.method || isPaymentLoading}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isPaymentLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <FiCheck className="w-4 h-4" />
                )}
                Complete Payment
              </button>
            </div>
          </div>
        </div>
      )}
      <OrderPaymentDrawer
        isOpen={paymentOrderId !== null}
        onClose={() => {setPaymentOrderId(null);refreshOrderData();}}
        orderId={paymentOrderId}
        onPaymentSuccess={handlePaymentSuccess}
        isFullWidth={true}
      />
    </div>
  );
};

export default PaymentAndInvoice;
